import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';
import Svg, { Defs, Path, Pattern, Rect } from 'react-native-svg';
import colors from '../../lib/colors';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, resolveMessage } from '../../lib/i18n/messages';

interface SystemDiagramProps {
    fontsLoaded: boolean;
    includeWorkInProgressItems?: boolean;
}

type SourceDotKind = 'preprint' | 'email' | 'custom' | 'data' | 'software' | 'patent';
type OutputTagKind = 'human' | 'agent';

type InputItem = { dot: SourceDotKind; label: LocalizedMessage; offset?: boolean; tag: LocalizedMessage; workInProgress?: boolean };
type OutputItem = { icon: string; label: LocalizedMessage; tag: LocalizedMessage; tagKind: OutputTagKind; workInProgress?: boolean };

const BASE_INPUT_ITEMS: InputItem[] = [
    { dot: 'preprint', label: msg({ id: 'source.iacr', message: 'IACR ePrint' }), tag: msg({ id: 'about.systemdiagram.preprint', message: 'preprint' }) },
    { dot: 'preprint', label: msg({ id: "about.systemdiagram.arxiv", message: "arXiv", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.preprint", message: "preprint", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'preprint', label: msg({ id: "about.systemdiagram.biorxiv", message: "bioRxiv", comment: "UI label in components/about/SystemDiagram.tsx" }), offset: true, tag: msg({ id: "about.systemdiagram.preprint", message: "preprint", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'preprint', label: msg({ id: "about.systemdiagram.medrxiv", message: "medRxiv", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.preprint", message: "preprint", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'preprint', label: msg({ id: "about.systemdiagram.chemrxiv", message: "ChemRxiv", comment: "UI label in components/about/SystemDiagram.tsx" }), offset: true, tag: msg({ id: "about.systemdiagram.preprint", message: "preprint", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'preprint', label: msg({ id: "about.systemdiagram.pubmed", message: "PubMed", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.indexedLiterature", message: "indexed literature", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'email', label: msg({ id: "about.systemdiagram.scholarAlerts", message: "Scholar Alerts", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.email", message: "email", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'email', label: msg({ id: "about.systemdiagram.journalAlerts", message: "Journal Alerts", comment: "UI label in components/about/SystemDiagram.tsx" }), offset: true, tag: msg({ id: "about.systemdiagram.email", message: "email", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
    { dot: 'custom', label: msg({ id: "about.systemdiagram.customSources", message: "Custom Sources", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.custom", message: "custom", comment: "UI tag in components/about/SystemDiagram.tsx" }) },
];

const WORK_IN_PROGRESS_INPUT_ITEMS: InputItem[] = [
    { dot: 'data', label: msg({ id: "about.systemdiagram.datasets", message: "Datasets", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.data", message: "data", comment: "UI tag in components/about/SystemDiagram.tsx" }), workInProgress: true },
    { dot: 'software', label: msg({ id: "about.systemdiagram.softwareRepos", message: "Software Repos", comment: "UI label in components/about/SystemDiagram.tsx" }), offset: true, tag: msg({ id: "about.systemdiagram.software", message: "software", comment: "UI tag in components/about/SystemDiagram.tsx" }), workInProgress: true },
    { dot: 'patent', label: msg({ id: "about.systemdiagram.patents", message: "Patents", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.patentsTag", message: "patents", comment: "UI tag in components/about/SystemDiagram.tsx" }), workInProgress: true },
];

const BASE_OUTPUT_ITEMS: OutputItem[] = [
    { icon: 'Feed', label: msg({ id: "about.systemdiagram.appFeed", message: "App Feed", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.human", message: "human", comment: "UI tag in components/about/SystemDiagram.tsx" }), tagKind: 'human' },
    { icon: 'Mail', label: msg({ id: "about.systemdiagram.emailDigest", message: "Email Digest", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.human", message: "human", comment: "UI tag in components/about/SystemDiagram.tsx" }), tagKind: 'human' },
    { icon: 'RSS', label: msg({ id: "about.systemdiagram.rssAtom", message: "RSS / Atom", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.human", message: "human", comment: "UI tag in components/about/SystemDiagram.tsx" }), tagKind: 'human' },
    { icon: 'MCP', label: msg({ id: "about.systemdiagram.mcpServer", message: "MCP Server", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.agent", message: "agent", comment: "UI tag in components/about/SystemDiagram.tsx" }), tagKind: 'agent' },
    { icon: '>', label: msg({ id: "about.systemdiagram.cliAccess", message: "CLI Access", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.agent", message: "agent", comment: "UI tag in components/about/SystemDiagram.tsx" }), tagKind: 'agent' },
];

const WORK_IN_PROGRESS_OUTPUT_ITEMS: OutputItem[] = [
    { icon: 'API', label: msg({ id: "about.systemdiagram.apiAccess", message: "API Access", comment: "UI label in components/about/SystemDiagram.tsx" }), tag: msg({ id: "about.systemdiagram.agent", message: "agent", comment: "UI tag in components/about/SystemDiagram.tsx" }), tagKind: 'agent', workInProgress: true },
];

function getSourceDotStyle(dot: SourceDotKind) {
    return dot === 'preprint' ? styles.preprintDot : dot === 'email' ? styles.emailDot : styles.customDot;
}

function getOutputTagStyle(tagKind: OutputTagKind) {
    return tagKind === 'human' ? styles.humanTag : styles.agentTag;
}

function PipelineArrow({ vertical }: { vertical: boolean }) {
    if (vertical) {
        return (
            <View style={styles.arrowVertical}>
                <Svg width={24} height={42} viewBox="0 0 24 42" fill="none">
                    <Path d="M12 2 L12 30" stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} strokeDasharray="3 3" />
                    <Path d="M7 27 L12 36 L17 27" stroke={colors.paleGreen} strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" />
                </Svg>
            </View>
        );
    }

    return (
        <View style={styles.arrowHorizontal}>
            <Svg width={40} height={24} viewBox="0 0 40 24" fill="none">
                <Path d="M2 12 L30 12" stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} strokeDasharray="3 3" />
                <Path d="M26 7 L34 12 L26 17" stroke={colors.paleGreen} strokeWidth={2} fill="none" strokeLinejoin="round" strokeLinecap="round" />
            </Svg>
        </View>
    );
}

function InputRow({ fontsLoaded, item }: { fontsLoaded: boolean; item: InputItem }) {
    const { i18n } = useLingui();
    return (
        <View style={[styles.sourceRow, item.offset && styles.sourceRowOffset]}>
            <View style={[styles.sourceDot, getSourceDotStyle(item.dot)]} />
            <Text style={[styles.sourceLabel, fontsLoaded && interMedium]}>
                {resolveMessage(i18n, item.label)}
                {item.workInProgress && <Text style={styles.workInProgressMark}>*</Text>}
            </Text>
            <Text style={[styles.sourceTag, fontsLoaded && interRegular]}>{resolveMessage(i18n, item.tag)}</Text>
        </View>
    );
}

function OutputRow({ fontsLoaded, item }: { fontsLoaded: boolean; item: OutputItem }) {
    const { i18n } = useLingui();
    return (
        <View style={styles.outputRow}>
            <Text style={[styles.outputIcon, fontsLoaded && interMedium]}>{item.icon}</Text>
            <Text style={[styles.outputLabel, fontsLoaded && interMedium]}>
                {resolveMessage(i18n, item.label)}
                {item.workInProgress && <Text style={styles.workInProgressMark}>*</Text>}
            </Text>
            <Text style={[styles.outputTag, getOutputTagStyle(item.tagKind), fontsLoaded && interSemiBold]}>
                {resolveMessage(i18n, item.tag)}
            </Text>
        </View>
    );
}

export default function SystemDiagram({ fontsLoaded, includeWorkInProgressItems = false }: SystemDiagramProps) {
    const { width } = useHydratedWindowDimensions();
    const isStacked = width < 920;
    const isNarrow = width < 520;
    const inputItems = includeWorkInProgressItems
        ? [...BASE_INPUT_ITEMS, ...WORK_IN_PROGRESS_INPUT_ITEMS]
        : BASE_INPUT_ITEMS;
    const outputItems = includeWorkInProgressItems
        ? [...BASE_OUTPUT_ITEMS, ...WORK_IN_PROGRESS_OUTPUT_ITEMS]
        : BASE_OUTPUT_ITEMS;

    return (
        <View style={[styles.diagram, isNarrow && styles.diagramNarrow]}>
            <Svg style={[styles.gridPattern, styles.noPointerEvents]} width="100%" height="100%">
                <Defs>
                    <Pattern id="about-system-grid" width={32} height={32} patternUnits="userSpaceOnUse">
                        <Path d="M32 0 L0 0 L0 32" fill="none" stroke="#ffffff" strokeWidth={0.5} />
                    </Pattern>
                </Defs>
                <Rect width="100%" height="100%" fill="url(#about-system-grid)" />
            </Svg>

            <View style={styles.diagramContent}>
                <View style={[styles.diagramLabels, isStacked && styles.hidden]}>
                    <Text style={[styles.diagramLabel, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.inputs" comment="UI copy in components/about/SystemDiagram.tsx">Inputs</Trans></Text>
                    <View />
                    <Text style={[styles.diagramLabel, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.relevanceEngine" comment="UI copy in components/about/SystemDiagram.tsx">Relevance Engine</Trans></Text>
                    <View />
                    <Text style={[styles.diagramLabel, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.outputs" comment="UI copy in components/about/SystemDiagram.tsx">Outputs</Trans></Text>
                </View>

                <View style={[styles.pipeline, isStacked && styles.pipelineStacked]}>
                    <View style={[styles.zone, isStacked && styles.zoneStacked]}>
                        <Text style={[styles.zoneMobileLabel, !isStacked && styles.hidden, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.inputs" comment="UI copy in components/about/SystemDiagram.tsx">Inputs</Trans></Text>
                        <Text style={[styles.zoneSubtitle, fontsLoaded && interSemiBold]}>
                            {includeWorkInProgressItems
                                ? <Trans id="about.systemdiagram.papersAlertsDatasetsSoftwarePatents" comment="Conditional UI copy in components/about/SystemDiagram.tsx">Papers, alerts, datasets, software, patents</Trans>
                                : <Trans id="about.systemdiagram.preprintsPubmedAlertsNewslettersCustomSources" comment="Conditional UI copy in components/about/SystemDiagram.tsx">Preprints, PubMed, alerts, newsletters, custom sources</Trans>}
                        </Text>
                        <View style={styles.sourceList}>
                            {inputItems.map((item) => (
                                <InputRow key={`${item.dot}-${String(typeof item.label === 'string' ? item.label : item.label.id)}`} fontsLoaded={fontsLoaded} item={item} />
                            ))}
                        </View>
                    </View>

                    <PipelineArrow vertical={isStacked} />

                    <View style={[styles.engine, isStacked && styles.zoneStacked]}>
                        <Text style={[styles.engineKicker, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.paperzilla" comment="UI copy in components/about/SystemDiagram.tsx">Paperzilla</Trans></Text>
                        <Text style={[styles.engineTitle, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.relevanceEngine" comment="UI copy in components/about/SystemDiagram.tsx">Relevance Engine</Trans></Text>

                        <View style={styles.engineList}>
                            <View style={styles.engineItem}>
                                <Text style={[styles.engineItemTitle, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.topicFilter" comment="UI copy in components/about/SystemDiagram.tsx">Topic filter</Trans></Text>
                                <Text style={[styles.engineItemText, fontsLoaded && interRegular]}><Trans id="about.systemdiagram.scopedToYourResearchInterest" comment="UI copy in components/about/SystemDiagram.tsx">Scoped to your research interest</Trans></Text>
                            </View>
                            <View style={styles.engineItem}>
                                <Text style={[styles.engineItemTitle, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.relevanceRanking" comment="UI copy in components/about/SystemDiagram.tsx">Relevance ranking</Trans></Text>
                                <Text style={[styles.engineItemText, fontsLoaded && interRegular]}><Trans id="about.systemdiagram.strongestMatchesRiseFirst" comment="UI copy in components/about/SystemDiagram.tsx">Strongest matches rise first</Trans></Text>
                            </View>
                            <View style={[styles.engineItem, styles.engineItemSpecial]}>
                                <Text style={[styles.engineItemTitle, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.feedbackLoop" comment="UI copy in components/about/SystemDiagram.tsx">Feedback loop</Trans></Text>
                                <Text style={[styles.engineItemText, fontsLoaded && interRegular]}><Trans id="about.systemdiagram.learnsWhatMattersOverTime" comment="UI copy in components/about/SystemDiagram.tsx">Learns what matters over time</Trans></Text>
                            </View>
                        </View>

                        <View style={[styles.feedbackNote, isStacked && styles.hidden]}>
                            <View style={styles.feedbackLine} />
                            <Text style={[styles.feedbackText, fontsLoaded && interRegular]}><Trans id="about.systemdiagram.improvesOverTime" comment="UI copy in components/about/SystemDiagram.tsx">improves over time</Trans></Text>
                        </View>
                    </View>

                    <PipelineArrow vertical={isStacked} />

                    <View style={[styles.zone, isStacked && styles.zoneStacked]}>
                        <Text style={[styles.zoneMobileLabel, !isStacked && styles.hidden, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.outputs" comment="UI copy in components/about/SystemDiagram.tsx">Outputs</Trans></Text>
                        <Text style={[styles.zoneSubtitle, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.feedsAndAccessForPeopleToolsAndAgents" comment="UI copy in components/about/SystemDiagram.tsx">
                            Feeds and access for people, tools, and agents
                        </Trans></Text>
                        <View style={styles.sourceList}>
                            {outputItems.map((item) => (
                                <OutputRow key={`${item.icon}-${String(typeof item.label === 'string' ? item.label : item.label.id)}`} fontsLoaded={fontsLoaded} item={item} />
                            ))}
                        </View>
                    </View>
                </View>

                <View style={[styles.stepLabels, isStacked && styles.hidden]}>
                    <View style={styles.stepLabel}>
                        <Text style={[styles.stepNumber, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.1" comment="UI copy in components/about/SystemDiagram.tsx">1</Trans></Text>
                        <Text style={[styles.stepText, fontsLoaded && interMedium]}><Trans id="about.systemdiagram.connectSources" comment="UI copy in components/about/SystemDiagram.tsx">Connect sources</Trans></Text>
                    </View>
                    {!isStacked && <View />}
                    <View style={styles.stepLabel}>
                        <Text style={[styles.stepNumber, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.2" comment="UI copy in components/about/SystemDiagram.tsx">2</Trans></Text>
                        <Text style={[styles.stepText, fontsLoaded && interMedium]}><Trans id="about.systemdiagram.tuneRelevance" comment="UI copy in components/about/SystemDiagram.tsx">Tune relevance</Trans></Text>
                    </View>
                    {!isStacked && <View />}
                    <View style={styles.stepLabel}>
                        <Text style={[styles.stepNumber, fontsLoaded && interSemiBold]}><Trans id="about.systemdiagram.3" comment="UI copy in components/about/SystemDiagram.tsx">3</Trans></Text>
                        <Text style={[styles.stepText, fontsLoaded && interMedium]}><Trans id="about.systemdiagram.getFocusedFeeds" comment="UI copy in components/about/SystemDiagram.tsx">Get focused feeds</Trans></Text>
                    </View>
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    diagram: {
        backgroundColor: colors.primaryGreenDark,
        borderRadius: 20,
        paddingHorizontal: 40,
        paddingVertical: 48,
        overflow: 'hidden',
        position: 'relative',
    },
    diagramNarrow: {
        borderRadius: 16,
        paddingHorizontal: 18,
        paddingVertical: 28,
    },
    gridPattern: {
        ...StyleSheet.absoluteFill,
        opacity: 0.07,
    },
    noPointerEvents: {
        pointerEvents: 'none',
    },
    diagramContent: {
        position: 'relative',
        zIndex: 1,
    },
    diagramLabels: {
        display: 'grid',
        gridTemplateColumns: '1fr 48px 1.3fr 48px 1fr',
        gap: 0,
        marginBottom: 24,
        alignItems: 'center',
        ...(Platform.OS !== 'web' && {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
        }),
    } as any,
    diagramLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 11,
        fontWeight: '700',
        textAlign: 'center',
        textTransform: 'uppercase',
    },
    pipeline: {
        display: 'grid',
        gridTemplateColumns: '1fr 48px 1.3fr 48px 1fr',
        alignItems: 'center',
        gap: 0,
        ...(Platform.OS !== 'web' && {
            display: 'flex',
            flexDirection: 'row',
        }),
    } as any,
    pipelineStacked: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    zone: {
        backgroundColor: 'rgba(255,255,255,0.07)',
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: 'rgba(255,255,255,0.12)',
        paddingHorizontal: 16,
        paddingVertical: 20,
    },
    zoneStacked: {
        width: '100%',
    },
    zoneMobileLabel: {
        color: 'rgba(255,255,255,0.52)',
        fontSize: 11,
        fontWeight: '700',
        marginBottom: 10,
        textAlign: 'center',
        textTransform: 'uppercase',
    },
    zoneSubtitle: {
        color: 'rgba(255,255,255,0.62)',
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 17,
        marginBottom: 12,
        textAlign: 'center',
    },
    sourceList: {
        gap: 7,
    },
    sourceRow: {
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        flexDirection: 'row',
        gap: 8,
        minHeight: 28,
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    sourceRowOffset: {
        transform: [{ translateX: 6 }],
    },
    sourceDot: {
        borderRadius: 999,
        height: 7,
        width: 7,
    },
    preprintDot: {
        backgroundColor: colors.primaryGreen,
    },
    emailDot: {
        backgroundColor: '#4f7fc2',
    },
    customDot: {
        backgroundColor: colors.darkGray,
    },
    sourceLabel: {
        color: '#ffffff',
        flex: 1,
        fontSize: 13,
        fontWeight: '500',
    },
    sourceTag: {
        color: 'rgba(255,255,255,0.38)',
        fontSize: 10,
    },
    arrowHorizontal: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    arrowVertical: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    engine: {
        backgroundColor: 'rgba(90,143,50,0.75)',
        borderColor: colors.paleGreen,
        borderRadius: 16,
        borderWidth: 2,
        paddingHorizontal: 24,
        paddingVertical: 28,
        position: 'relative',
    },
    engineKicker: {
        color: colors.veryLightGreen,
        fontSize: 11,
        fontWeight: '700',
        marginBottom: 8,
        textAlign: 'center',
        textTransform: 'uppercase',
    },
    engineTitle: {
        color: '#ffffff',
        fontSize: 18,
        fontWeight: '800',
        lineHeight: 22,
        marginBottom: 20,
        textAlign: 'center',
    },
    engineList: {
        gap: 10,
    },
    engineItem: {
        backgroundColor: 'rgba(0,0,0,0.2)',
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 10,
    },
    engineItemSpecial: {
        backgroundColor: 'rgba(255,255,255,0.18)',
        borderColor: 'rgba(255,255,255,0.3)',
        borderWidth: 1,
    },
    engineItemTitle: {
        color: '#ffffff',
        fontSize: 13,
        fontWeight: '700',
    },
    engineItemText: {
        color: 'rgba(255,255,255,0.62)',
        fontSize: 11,
        marginTop: 2,
    },
    feedbackNote: {
        alignItems: 'center',
        bottom: -18,
        left: '50%',
        position: 'absolute',
        transform: [{ translateX: -60 }],
        width: 120,
    },
    feedbackLine: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        height: 12,
        width: 1,
    },
    feedbackText: {
        color: 'rgba(255,255,255,0.42)',
        fontSize: 10,
    },
    outputRow: {
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.08)',
        borderRadius: 8,
        flexDirection: 'row',
        gap: 10,
        minHeight: 30,
        paddingHorizontal: 12,
        paddingVertical: 7,
    },
    outputIcon: {
        color: colors.paleGreen,
        fontSize: 10,
        fontWeight: '600',
        textAlign: 'center',
        width: 28,
    },
    outputLabel: {
        color: '#ffffff',
        flex: 1,
        fontSize: 13,
        fontWeight: '500',
    },
    outputTag: {
        borderRadius: 4,
        fontSize: 10,
        fontWeight: '700',
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    humanTag: {
        backgroundColor: 'rgba(90,143,50,0.35)',
        color: colors.veryLightGreen,
    },
    agentTag: {
        backgroundColor: 'rgba(100,150,255,0.2)',
        color: '#aac4ff',
    },
    workInProgressMark: {
        color: colors.paleGreen,
        fontSize: 11,
        fontWeight: '800',
        marginLeft: 1,
    },
    stepLabels: {
        display: 'grid',
        gridTemplateColumns: '1fr 48px 1.3fr 48px 1fr',
        gap: 0,
        marginTop: 32,
        ...(Platform.OS !== 'web' && {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
        }),
    } as any,
    stepLabel: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'center',
    },
    stepNumber: {
        alignItems: 'center',
        backgroundColor: colors.primaryGreen,
        borderRadius: 999,
        color: '#ffffff',
        display: 'flex',
        fontSize: 11,
        fontWeight: '700',
        height: 22,
        justifyContent: 'center',
        lineHeight: 22,
        textAlign: 'center',
        width: 22,
    },
    stepText: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12,
        fontWeight: '500',
    },
    hidden: {
        display: 'none',
    },
});
